import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Check, X, Loader2, DollarSign, BadgeCheck, Gift, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin · vendor Spotlight promotions — approval queue, comps, slot pricing and
 * the artwork sizes each slot expects.
 */
export default function AdminSpotlightPromotions() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [pending, setPending] = useState([]);
  const [awaitingPayment, setAwaitingPayment] = useState([]);

  const load = useCallback(async () => {
    try {
      const [pr, unpaid] = await Promise.all([
        axios.get(`${API}/admin/spotlight-promotions?status=paid_pending_approval`, { headers }),
        axios.get(`${API}/admin/spotlight-promotions?status=pending_payment`, { headers }),
      ]);
      setPending(pr.data.promotions || []);
      setAwaitingPayment(unpaid.data.promotions || []);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await axios.post(`${API}/admin/spotlight-promotions/${id}/approve`, {}, { headers }); toast.success('Approved — now live'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
  };
  const comp = async (id) => {
    if (!window.confirm('Run this promotion free of charge? Any payment already taken is refunded.')) return;
    try {
      const res = await axios.post(`${API}/admin/spotlight-promotions/${id}/comp`, {}, { headers });
      toast.success(res.data.refunded ? 'Running free — payment refunded' : 'Running free of charge');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not comp this promotion'); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try {
      const res = await axios.post(`${API}/admin/spotlight-promotions/${id}/reject`, { reason }, { headers });
      if (res.data.refunded) toast.success('Rejected & refunded to the vendor');
      else if (res.data.refund_error) toast.warning('Rejected — refund could not be processed automatically (refund manually in Stripe)');
      else toast.success('Rejected');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
  };

  const PromoRow = ({ p, unpaid }) => (
    <Card key={p.id} className={`bg-white/5 ${unpaid ? 'border-white/10' : 'border-blue-500/20'}`} data-testid={`pending-promo-${p.id}`}>
      <CardContent className="py-3 flex items-center gap-4 flex-wrap">
        {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 rounded object-cover" />}
        <div className="flex-1 min-w-0">
          <p className="text-gray-100 font-medium truncate">{p.title}</p>
          <p className="text-xs text-gray-400">
            {p.entity_type} · {p.slot} · {p.run_label || `${p.duration_days}d`} · ${p.price}
            {unpaid && <span className="text-amber-300"> · not paid yet</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {!unpaid && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => approve(p.id)} data-testid={`approve-promo-${p.id}`}>
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-8 border-amber-400/40 text-amber-200 hover:bg-amber-500/10" onClick={() => comp(p.id)} data-testid={`comp-promo-${p.id}`}>
            <Gift className="w-4 h-4 mr-1" /> Run free
          </Button>
          <Button size="sm" variant="outline" className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => reject(p.id)} data-testid={`reject-promo-${p.id}`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5 mt-6 pt-6 border-t border-white/10" data-testid="admin-spotlight-promotions">
      {/* Approval queue */}
      <div>
        <h3 className="text-base font-semibold text-gray-100 flex items-center gap-2 mb-3">
          <BadgeCheck className="w-5 h-5 text-blue-300" /> Vendor Promotions — Approval Queue
          {pending.length > 0 && <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">{pending.length}</span>}
        </h3>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-500">No promotions awaiting approval.</p>
        ) : (
          <div className="space-y-2">{pending.map((p) => <PromoRow key={p.id} p={p} />)}</div>
        )}
      </div>

      {/* Unpaid — can still be comped when a slot is going spare */}
      {awaitingPayment.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-gray-100 flex items-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-amber-300" /> Started but not paid
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Use &quot;Run free&quot; to give someone the slot at no charge — handy when the space would sit empty anyway.
          </p>
          <div className="space-y-2">{awaitingPayment.map((p) => <PromoRow key={p.id} p={p} unpaid />)}</div>
        </div>
      )}

      {/* Slot pricing moved into the Ad Spaces block (session 62) — one place per space. */}
    </div>
  );
}
